import React, { useState, useEffect, useMemo } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Image as ImageIcon, PlusCircle, Edit, Trash2, Power, ExternalLink, Search,
  TrendingUp, MousePointerClick, Calendar as CalendarIcon, Building2, Users,
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = '';

// Page list per Feb-2026 product spec. Keys are the placement strings
// stored against each banner — they match the values that <SponsorBanner>
// passes when fetching banners on each page, so an admin who picks
// "Enablers – Events" will see the banner appear on the Events list page.
const PLACEMENT_OPTIONS = [
  { value: 'app',                 label: 'App-wide (every page)' },
  { value: 'progress',            label: 'Progress' },
  { value: 'enablers-events',     label: 'Enablers – Events' },
  { value: 'events-descriptor',   label: 'Events – Descriptor' },
  { value: 'enablers-courses',    label: 'Enablers – Courses' },
  { value: 'courses-descriptor',  label: 'Courses – Descriptor' },
  { value: 'keynotes',            label: 'Keynotes' },
  { value: 'britecast',           label: 'BriteCast' },
  { value: 'learning',            label: 'Learning' },
  { value: 'news',                label: 'News' },
  { value: 'news-article',        label: 'News – Article' },
  { value: 'companies',           label: 'Companies' },
  { value: 'jobs',                label: 'Jobs' },
  { value: 'forum',               label: 'Forum' },
  { value: 'forum-2',             label: 'Forum – Side Banner 2' },
  { value: 'forum-inline',        label: 'Forum – Inline (every 10 posts)' },
  { value: 'britelab',            label: 'BriteLab' },
  { value: 'members',             label: 'Members' },
  { value: 'partners',            label: 'Partners' },
  ...[
    ['app', 'App-wide'], ['progress', 'Progress'], ['enablers-events', 'Enablers – Events'],
    ['events-descriptor', 'Events – Descriptor'], ['enablers-courses', 'Enablers – Courses'],
    ['courses-descriptor', 'Courses – Descriptor'], ['keynotes', 'Keynotes'], ['britecast', 'BriteCast'], ['learning', 'Learning'],
    ['news', 'News'], ['news-article', 'News – Article'], ['companies', 'Companies'], ['jobs', 'Jobs'],
    ['britelab', 'BriteLab'], ['members', 'Members'], ['partners', 'Partners'],
  ].map(([v, l]) => ({ value: `${v}-2`, label: `${l} – Side Banner 2` })),
];

const STATUS_PILL = {
  live: 'bg-green-100 text-green-700',
  upcoming: 'bg-blue-100 text-blue-700',
  paused: 'bg-slate-200 text-slate-600',
  expired: 'bg-red-100 text-red-700',
};

const emptyForm = {
  sponsor_id: '',
  banner_type: 'top',
  placement: 'app',
  click_url: '',
  campaign_start: '',
  campaign_end: '',
  file: null,
};

export function AdminBannersTab() {
  const [analytics, setAnalytics] = useState({ banners: [], total_impressions: 0, total_clicks: 0 });
  const [clickersBanner, setClickersBanner] = useState(null);
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterSponsor, setFilterSponsor] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ ...emptyForm });

  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});

  const loadData = async () => {
    setLoading(true);
    try {
      const [aRes, sRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/banners/analytics`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/sponsors?include_partners=true`, { credentials: 'include' }),
      ]);
      if (aRes.ok) setAnalytics(await aRes.json());
      if (sRes.ok) {
        const list = await sRes.json();
        const arr = Array.isArray(list) ? list : (list.sponsors || []);
        setSponsors([...arr].sort((a, b) => (a.name || '').localeCompare(b.name || '')));
      }
    } catch { toast.error('Failed to load banners'); }
    setLoading(false);
  };

  useEffect(() => { loadData(); }, []);

  const filtered = useMemo(() => {
    return analytics.banners.filter(b => {
      if (filterSponsor && b.sponsor_id !== filterSponsor) return false;
      if (filterStatus && b.campaign_status !== filterStatus) return false;
      if (search && !(b.sponsor_name || '').toLowerCase().includes(search.toLowerCase())
        && !(b.placement || '').toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [analytics.banners, filterSponsor, filterStatus, search]);

  const handleCreate = async () => {
    if (!createForm.sponsor_id) { toast.error('Pick a sponsor / partner'); return; }
    if (!createForm.file) { toast.error('Select a banner image'); return; }
    const fd = new FormData();
    fd.append('file', createForm.file);
    fd.append('banner_type', createForm.banner_type);
    fd.append('placement', createForm.placement);
    fd.append('click_url', createForm.click_url || '');
    if (createForm.campaign_start) fd.append('campaign_start', createForm.campaign_start);
    if (createForm.campaign_end) fd.append('campaign_end', createForm.campaign_end);
    try {
      const res = await fetch(`${BACKEND_URL}/api/sponsors/${createForm.sponsor_id}/banners`, {
        method: 'POST', credentials: 'include', body: fd,
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.detail || 'Failed');
      }
      toast.success('Banner created');
      setShowCreate(false);
      setCreateForm({ ...emptyForm });
      loadData();
    } catch (e) { toast.error(e.message); }
  };

  const handleToggleEnabled = async (b) => {
    try {
      const fd = new FormData();
      fd.append('enabled', String(!b.enabled));
      const res = await fetch(`${BACKEND_URL}/api/sponsors/banners/${b.banner_id}`, {
        method: 'PUT', credentials: 'include', body: fd,
      });
      if (!res.ok) throw new Error('Failed');
      toast.success(b.enabled ? 'Banner paused' : 'Banner enabled');
      loadData();
    } catch { toast.error('Failed to toggle'); }
  };

  const handleDelete = async (b) => {
    if (!(await window.appConfirm(`Delete banner for "${b.sponsor_name}"? This cannot be undone.`))) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/sponsors/banners/${b.banner_id}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed');
      toast.success('Banner deleted');
      loadData();
    } catch { toast.error('Failed to delete'); }
  };

  const openEdit = (b) => {
    setEditing(b);
    setEditForm({
      banner_type: b.banner_type || 'top',
      placement: b.placement || 'app',
      click_url: b.click_url || '',
      campaign_start: b.campaign_start || '',
      campaign_end: b.campaign_end || '',
      file: null,
    });
  };

  const handleSaveEdit = async () => {
    if (!editing) return;
    const fd = new FormData();
    fd.append('banner_type', editForm.banner_type);
    fd.append('placement', editForm.placement);
    fd.append('click_url', editForm.click_url || '');
    fd.append('campaign_start', editForm.campaign_start || '');
    fd.append('campaign_end', editForm.campaign_end || '');
    if (editForm.file) fd.append('file', editForm.file);
    try {
      const res = await fetch(`${BACKEND_URL}/api/sponsors/banners/${editing.banner_id}`, {
        method: 'PUT', credentials: 'include', body: fd,
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Failed'); }
      toast.success('Banner updated');
      setEditing(null);
      loadData();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-4" data-testid="admin-banners-tab">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <ImageIcon className="h-5 w-5 text-brand-navy" /> Banner Ads
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">Manage every sponsor / partner banner across the platform.</p>
        </div>
        <Button data-testid="create-banner-btn"
          onClick={() => setShowCreate(true)}
          className="bg-gradient-to-r from-brand-navy to-brand-sage text-white">
          <PlusCircle className="h-4 w-4 mr-2" /> New Banner
        </Button>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Total banners" value={analytics.banners.length} icon={ImageIcon} color="text-brand-navy" />
        <StatCard label="Live" value={analytics.banners.filter(b => b.campaign_status === 'live').length} icon={Power} color="text-green-600" />
        <StatCard label="Impressions" value={analytics.total_impressions.toLocaleString()} icon={TrendingUp} color="text-blue-600" />
        <StatCard label="Clicks" value={analytics.total_clicks.toLocaleString()} icon={MousePointerClick} color="text-amber-600" />
      </div>

      {/* Filters */}
      <Card className="p-3 flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="banner-search"
            placeholder="Search by sponsor or placement…"
            className="pl-8 h-9 text-sm"
            value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="border border-slate-200 rounded-md px-2 py-1.5 text-xs bg-white" data-testid="filter-sponsor"
          value={filterSponsor} onChange={e => setFilterSponsor(e.target.value)}>
          <option value="">All sponsors</option>
          {sponsors.map(s => <option key={s.sponsor_id} value={s.sponsor_id}>{s.name}</option>)}
        </select>
        <select className="border border-slate-200 rounded-md px-2 py-1.5 text-xs bg-white" data-testid="filter-status"
          value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">All statuses</option>
          <option value="live">Live</option>
          <option value="upcoming">Upcoming</option>
          <option value="paused">Paused</option>
          <option value="expired">Expired</option>
        </select>
      </Card>

      {/* Banners table */}
      {loading ? (
        <p className="text-center text-sm text-slate-400 py-8">Loading banners…</p>
      ) : filtered.length === 0 ? (
        <Card className="p-8 text-center" data-testid="banners-empty-state">
          <ImageIcon className="h-10 w-10 text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-500">
            {analytics.banners.length === 0
              ? 'No banners yet. Create the first one to begin.'
              : 'No banners match your filters.'}
          </p>
        </Card>
      ) : (
        <div className="space-y-2" data-testid="banners-list">
          {filtered.map(b => (
            <BannerRow key={b.banner_id}
              banner={b}
              onToggle={() => handleToggleEnabled(b)}
              onEdit={() => openEdit(b)}
              onDelete={() => handleDelete(b)}
              onShowClickers={() => setClickersBanner(b)}
            />
          ))}
        </div>
      )}

      {/* Clickers dialog — surfaces WHO clicked the banner (not just how
          many). Anonymous (logged-out) clicks rolled up into one row. */}
      <ClickersDialog banner={clickersBanner} onClose={() => setClickersBanner(null)} />

      {/* Create dialog */}
      <Dialog open={showCreate} onOpenChange={(o) => { setShowCreate(o); if (!o) setCreateForm({ ...emptyForm }); }}>
        <DialogContent className="max-w-lg" data-testid="banner-create-dialog">
          <DialogHeader>
            <DialogTitle>New Banner</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Sponsor / Partner *</Label>
              <select className="w-full mt-1 border border-slate-200 rounded-md px-2 py-2 text-sm bg-white" data-testid="create-banner-sponsor"
                value={createForm.sponsor_id}
                onChange={e => setCreateForm(f => ({ ...f, sponsor_id: e.target.value }))}>
                <option value="">— pick —</option>
                {sponsors.map(s => <option key={s.sponsor_id} value={s.sponsor_id}>{s.name}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Banner type</Label>
                <select className="w-full mt-1 border border-slate-200 rounded-md px-2 py-2 text-sm bg-white" data-testid="create-banner-type"
                  value={createForm.banner_type}
                  onChange={e => setCreateForm(f => ({ ...f, banner_type: e.target.value }))}>
                  <option value="top">Top</option>
                  <option value="side">Side</option>
                  <option value="footer">Bottom Banner</option>
                </select>
              </div>
              <div>
                <Label>Placement</Label>
                <select className="w-full mt-1 border border-slate-200 rounded-md px-2 py-2 text-sm bg-white" data-testid="create-banner-placement"
                  value={createForm.placement}
                  onChange={e => setCreateForm(f => ({ ...f, placement: e.target.value }))}>
                  {PLACEMENT_OPTIONS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </div>
            </div>
            <div>
              <Label>Click URL</Label>
              <Input data-testid="create-banner-url" placeholder="https://…" value={createForm.click_url}
                onChange={e => setCreateForm(f => ({ ...f, click_url: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Campaign start</Label>
                <Input type="date" data-testid="create-banner-start" value={createForm.campaign_start}
                  onChange={e => setCreateForm(f => ({ ...f, campaign_start: e.target.value }))} />
              </div>
              <div>
                <Label>Campaign end</Label>
                <Input type="date" data-testid="create-banner-end" value={createForm.campaign_end}
                  onChange={e => setCreateForm(f => ({ ...f, campaign_end: e.target.value }))} />
              </div>
            </div>
            <div>
              <Label>Banner image *</Label>
              <input type="file" accept="image/*" data-testid="create-banner-file"
                onChange={e => setCreateForm(f => ({ ...f, file: e.target.files?.[0] || null }))}
                className="text-sm w-full mt-1" />
              <p className="text-[10px] text-slate-400 mt-0.5">Max 5 MB. PNG or JPG recommended.</p>
            </div>
            <Button onClick={handleCreate} className="w-full bg-brand-navy text-white" data-testid="create-banner-submit">
              Create Banner
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent className="max-w-lg" data-testid="banner-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit Banner — {editing?.sponsor_name}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Banner type</Label>
                  <select className="w-full mt-1 border border-slate-200 rounded-md px-2 py-2 text-sm bg-white"
                    value={editForm.banner_type}
                    onChange={e => setEditForm(f => ({ ...f, banner_type: e.target.value }))}>
                    <option value="top">Top</option>
                    <option value="side">Side</option>
                    <option value="footer">Bottom Banner</option>
                  </select>
                </div>
                <div>
                  <Label>Placement</Label>
                  <select className="w-full mt-1 border border-slate-200 rounded-md px-2 py-2 text-sm bg-white"
                    value={editForm.placement}
                    onChange={e => setEditForm(f => ({ ...f, placement: e.target.value }))}>
                    {PLACEMENT_OPTIONS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <Label>Click URL</Label>
                <Input value={editForm.click_url || ''}
                  onChange={e => setEditForm(f => ({ ...f, click_url: e.target.value }))} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Campaign start</Label>
                  <Input type="date" value={editForm.campaign_start || ''}
                    onChange={e => setEditForm(f => ({ ...f, campaign_start: e.target.value }))} />
                </div>
                <div>
                  <Label>Campaign end</Label>
                  <Input type="date" value={editForm.campaign_end || ''}
                    onChange={e => setEditForm(f => ({ ...f, campaign_end: e.target.value }))} />
                </div>
              </div>
              <div>
                <Label>Replace image (optional)</Label>
                <input type="file" accept="image/*"
                  onChange={e => setEditForm(f => ({ ...f, file: e.target.files?.[0] || null }))}
                  className="text-sm w-full mt-1" />
              </div>
              <Button onClick={handleSaveEdit} className="w-full bg-brand-navy text-white" data-testid="edit-banner-submit">
                Save Changes
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color }) {
  return (
    <Card className="p-3 md:p-4 border-0 shadow-sm">
      <div className="flex items-center gap-3">
        <Icon className={`h-5 w-5 ${color}`} />
        <div>
          <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-400">{label}</p>
          <p className="text-xl font-bold text-slate-800">{value}</p>
        </div>
      </div>
    </Card>
  );
}

function BannerRow({ banner, onToggle, onEdit, onDelete, onShowClickers }) {
  const [imgUrl, setImgUrl] = useState('');
  // Lazy-load the actual image (admin analytics endpoint omits image_url for size)
  useEffect(() => {
    fetch(`${BACKEND_URL}/api/sponsors/${banner.sponsor_id}/banners`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : [])
      .then(list => {
        const match = (list || []).find(x => x.banner_id === banner.banner_id);
        if (match) setImgUrl(match.image_url || '');
      })
      .catch(() => {});
  }, [banner.banner_id, banner.sponsor_id]);

  const ctr = banner.ctr || 0;
  const pillCls = STATUS_PILL[banner.campaign_status] || 'bg-slate-200 text-slate-600';

  return (
    <Card className="p-3 flex gap-3 items-center" data-testid={`banner-row-${banner.banner_id}`}>
      {/* Thumb */}
      <div className="w-20 h-12 md:w-28 md:h-16 bg-slate-100 rounded overflow-hidden flex-shrink-0 flex items-center justify-center">
        {imgUrl ? (
          <img src={imgUrl} alt={banner.sponsor_name} className="w-full h-full object-cover" />
        ) : (
          <ImageIcon className="h-5 w-5 text-slate-300" />
        )}
      </div>

      {/* Meta */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-0.5">
          <p className="text-sm font-semibold text-slate-800 truncate flex items-center gap-1.5">
            <Building2 className="h-3 w-3 text-slate-400" /> {banner.sponsor_name}
          </p>
          <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${pillCls}`} data-testid={`banner-status-${banner.banner_id}`}>
            {banner.campaign_status}
          </span>
          <span className="text-[10px] uppercase tracking-wider font-medium text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
            {banner.banner_type} · {banner.placement}
          </span>
        </div>
        <div className="text-[11px] text-slate-500 flex flex-wrap gap-x-3 gap-y-0.5">
          {(banner.campaign_start || banner.campaign_end) && (
            <span className="flex items-center gap-1">
              <CalendarIcon className="h-3 w-3" />
              {banner.campaign_start || '—'} → {banner.campaign_end || '—'}
            </span>
          )}
          <span><TrendingUp className="h-3 w-3 inline mr-0.5" />{(banner.impressions || 0).toLocaleString()} imp</span>
          <span><MousePointerClick className="h-3 w-3 inline mr-0.5" />{(banner.clicks || 0).toLocaleString()} clicks</span>
          <span className="font-medium text-slate-600">CTR {ctr}%</span>
          <button
            type="button"
            onClick={onShowClickers}
            data-testid={`banner-view-clickers-${banner.banner_id}`}
            className="inline-flex items-center gap-1 text-brand-navy hover:text-brand-dark hover:underline font-medium"
            disabled={(banner.clicks || 0) === 0}
            title={(banner.clicks || 0) === 0 ? 'No clicks yet' : 'See who clicked'}
          >
            <Users className="h-3 w-3" /> View clickers
          </button>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 flex-shrink-0">
        {banner.click_url && (
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" title="Open click URL"
            asChild data-testid={`banner-open-${banner.banner_id}`}>
            <a href={banner.click_url} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </Button>
        )}
        <Button variant="ghost" size="sm" className={`h-8 w-8 p-0 ${banner.enabled ? 'text-green-600' : 'text-slate-400'}`}
          title={banner.enabled ? 'Pause' : 'Enable'} onClick={onToggle}
          data-testid={`banner-toggle-${banner.banner_id}`}>
          <Power className="h-3.5 w-3.5" />
        </Button>
        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={onEdit}
          data-testid={`banner-edit-${banner.banner_id}`}>
          <Edit className="h-3.5 w-3.5" />
        </Button>
        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-500"
          onClick={onDelete}
          data-testid={`banner-delete-${banner.banner_id}`}>
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </Card>
  );
}


const BACKEND_URL_FOR_CLICKERS = process.env.REACT_APP_BACKEND_URL;

/**
 * ClickersDialog — fetches /api/admin/banners/{id}/clickers when opened and
 * shows a list of every member who clicked the banner, with their click
 * count and last-clicked timestamp. Anonymous (logged-out) clicks roll up
 * into a single summary row.
 */
function ClickersDialog({ banner, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const open = !!banner;
  useEffect(() => {
    if (!open || !banner?.banner_id) return;
    setLoading(true);
    setData(null);
    fetch(`${BACKEND_URL_FOR_CLICKERS}/api/admin/banners/${banner.banner_id}/clickers`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => setData(d))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [open, banner?.banner_id]);
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="banner-clickers-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">
            Clickers — <span className="text-brand-navy">{banner?.title || banner?.banner_id}</span>
          </DialogTitle>
        </DialogHeader>
        {loading && <p className="text-sm text-slate-500 py-6 text-center">Loading…</p>}
        {!loading && data && (
          <>
            <div className="flex flex-wrap items-center gap-3 text-xs mb-3">
              <span className="px-2 py-1 rounded-full bg-blue-50 text-blue-700 font-semibold">
                {data.total_clicks.toLocaleString()} total clicks
              </span>
              <span className="px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 font-semibold">
                {data.unique_clickers.toLocaleString()} unique members
              </span>
              {data.anonymous_clicks > 0 && (
                <span className="px-2 py-1 rounded-full bg-slate-100 text-slate-600 font-semibold">
                  {data.anonymous_clicks.toLocaleString()} anonymous (logged-out)
                </span>
              )}
            </div>
            {data.clickers.length === 0 && data.anonymous_clicks === 0 && (
              <p className="text-sm text-slate-500 py-6 text-center">No clicks captured yet.</p>
            )}
            {data.clickers.length > 0 && (
              <div className="space-y-1.5">
                {data.clickers.map(c => (
                  <div key={c.user_id}
                    data-testid={`clicker-row-${c.user_id}`}
                    className="flex flex-col sm:flex-row sm:items-center gap-2 p-2.5 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-baseline gap-x-2">
                        <p className="text-sm font-semibold text-slate-800 truncate">{c.name}</p>
                        {c.email && <p className="text-xs text-slate-500 truncate">{c.email}</p>}
                      </div>
                      {(c.role || c.organisation) && (
                        <p className="text-[11px] text-slate-500">
                          {c.role}{c.role && c.organisation ? ' · ' : ''}{c.organisation}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-0.5 flex-shrink-0">
                      <span className="text-sm font-bold text-brand-navy">
                        {c.click_count} click{c.click_count !== 1 ? 's' : ''}
                      </span>
                      <span className="text-[10px] text-slate-400">
                        Last {new Date(c.last_clicked_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
